import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plane, HeartPulse } from "lucide-react";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function eventColor(ev) {
  if (ev.kind === "sick") return "bg-rose-100 text-rose-700 border-rose-200";
  if ((ev.leave_type || "").toLowerCase().includes("annual")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  return "bg-sky-100 text-sky-700 border-sky-200";
}

export default function LeaveCalendar() {
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [empFilter, setEmpFilter] = useState("all");
  const monthStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leave-calendar", { params: { month: monthStr } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [monthStr]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7; // Monday-start offset
    const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const employees = useMemo(() => Array.from(new Set(events.map((e) => e.employee_name).filter(Boolean))).sort(), [events]);
  const visibleEvents = useMemo(() => events.filter((ev) => {
    const t = ev.kind === "sick" ? "sick" : ((ev.leave_type || "").toLowerCase().includes("annual") ? "annual" : "other");
    const typeOk = typeFilter === "all" || typeFilter === t;
    const empOk = empFilter === "all" || ev.employee_name === empFilter;
    return typeOk && empOk;
  }), [events, typeFilter, empFilter]);

  const eventsForDay = (date) => {
    const s = ymd(date);
    return visibleEvents.filter((ev) => ev.start <= s && (ev.end || ev.start) >= s);
  };

  const todayStr = ymd(new Date());
  const onLeaveCount = new Set(visibleEvents.map((e) => e.employee_name)).size;

  return (
    <AdminLayout nav={adminNav} title="Leave Calendar">
      <PageHeader title="Leave Calendar" subtitle="Everyone's approved holidays and sick days at a glance" />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
          <div data-testid="cal-month" className="font-heading font-bold text-lg w-48 text-center">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <Button data-testid="cal-next" variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          <Button data-testid="cal-today" variant="ghost" size="sm" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}>Today</Button>
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-emerald-200 border border-emerald-300" /> Annual leave</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-sky-200 border border-sky-300" /> Other leave</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-rose-200 border border-rose-300" /> Sick</span>
          <span data-testid="cal-count" className="font-semibold text-zinc-700">{onLeaveCount} on leave this month</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="cal-type-filter" className="w-44 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All leave types</SelectItem>
            <SelectItem value="annual">Annual leave</SelectItem>
            <SelectItem value="other">Other leave</SelectItem>
            <SelectItem value="sick">Sick</SelectItem>
          </SelectContent>
        </Select>
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger data-testid="cal-emp-filter" className="w-56 h-9"><SelectValue placeholder="All employees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employees.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        {(typeFilter !== "all" || empFilter !== "all") && (
          <Button data-testid="cal-clear-filters" variant="ghost" size="sm" onClick={() => { setTypeFilter("all"); setEmpFilter("all"); }}>Clear filters</Button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
          {WD.map((d) => <div key={d} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((date, i) => {
            if (!date) return <div key={i} className="min-h-[104px] border-b border-r border-zinc-100 bg-zinc-50/50" />;
            const evs = eventsForDay(date);
            const isToday = ymd(date) === todayStr;
            return (
              <div key={i} data-testid={`cal-day-${ymd(date)}`} className="min-h-[104px] border-b border-r border-zinc-100 p-1.5 flex flex-col">
                <div className={`text-xs font-semibold mb-1 ${isToday ? "h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center" : "text-zinc-400"}`}>{date.getDate()}</div>
                <div className="space-y-1 overflow-hidden">
                  {evs.slice(0, 3).map((ev, j) => (
                    <div key={j} title={`${ev.employee_name} — ${ev.leave_type}`} className={`text-[10px] leading-tight px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${eventColor(ev)}`}>
                      {ev.kind === "sick" ? <HeartPulse className="h-2.5 w-2.5 shrink-0" /> : <Plane className="h-2.5 w-2.5 shrink-0" />}
                      <span className="truncate">{ev.employee_name}</span>
                    </div>
                  ))}
                  {evs.length > 3 && <div className="text-[10px] text-zinc-400 px-1">+{evs.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {loading && <div className="text-sm text-zinc-400 mt-3">Loading…</div>}
      {!loading && visibleEvents.length === 0 && (
        <div className="text-sm text-zinc-400 mt-3" data-testid="cal-empty">
          {events.length === 0 ? "No approved leave or sick days this month." : "No leave matches the current filters."}
        </div>
      )}
    </AdminLayout>
  );
}
